import {Button, MenuItem, useMediaQuery} from '@material-ui/core';
import {AccountSourceType, getAccountDetailsQueryParams} from 'Agent/Accounts/AccountRouteConstants';
import {
  changeAccountFilters,
  changeSort,
  clearAccounts,
  clearSort,
  FilterEnum,
  getAccounts,
} from 'Agent/Accounts/ducks/account';
import PolicyRouteConstants from 'Agent/Policies/PolicyRouteConstants';
import {theme} from 'Core/components/WithTheme';
import {ModalState, showModal} from 'Core/ducks/modal';
import * as O from 'fp-ts/lib/Option';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Link, useHistory, useLocation} from 'react-router-dom';
import {ReducerState} from 'rootReducer';
import FileDownloadButton, {FileExtension} from 'Shared/components/FileDownloadButton';
import FileDownloadFab from 'Shared/components/FileDownloadFab';
import ActionsMenu from 'Shared/components/PagedView/ActionsMenu';
import Column from 'Shared/components/PagedView/Column';
import {
  withDateFilterProperties,
  withSelectFilterProperties,
  withStringFilterProperties,
} from 'Shared/components/PagedView/Filter';
import PagedView from 'Shared/components/PagedView/PagedView';
import RequireScope from 'Shared/components/RequireScope';
import {Scope} from 'Shared/constants/Scope';
import {AgencyFilters} from 'Shared/models/AgencyFilters';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import {DataSource, getEisFlagFromLinks} from 'Shared/utilities/DataSourceUtility';
import {toFormattedDateAsUTC} from 'Shared/utilities/DateFormatter';
import {parseQueryString} from 'Shared/utilities/parsers';
import {prop} from 'Shared/utilities/prop';
import {toSentenceCase} from 'Shared/utilities/stringFormatHelpers';
import {withValueChangedHook} from 'Shared/utilities/withValueChanged';
import {selectUserScopes} from 'Users/selectors/userProfileSelectors';
import {changeAnalyticsState} from 'Core/ducks/analytics';
import {ANALYTICS_EVENT_ACTION} from 'Core/components/Analytics/AnalyticsEventConstants';
import {changeCorrespondenceFilter} from 'Agent/Policies/ducks/policyCorrespondence';
import {getCustomerConnectionProfile} from '../ducks/a360EnrollmentApplication';

const AccountStatuses: FilterEnum[] = [
  {
    name: 'Inactive',
    value: 'Inactive',
  },
  {
    name: 'Active',
    value: 'Active',
  },
];

const PaymentStatuses: FilterEnum[] = [
  {
    name: 'Delinquent',
    value: '1',
  },
  {
    name: 'Discrepant',
    value: '2',
  },
  {
    name: 'Pending',
    value: '3',
  },
  {
    name: 'Current',
    value: '4',
  },
  {
    name: 'Draft',
    value: '5',
  },
  {
    name: 'Unapplied',
    value: '6',
  },
  {
    name: 'Allocated',
    value: '7',
  },
  {
    name: 'Partially Allocated',
    value: '8',
  },
  {
    name: 'Declined',
    value: '9',
  },
  {
    name: 'Reversed',
    value: '10',
  },
  {
    name: 'Suspended',
    value: '11',
  },
  {
    name: 'Transferred',
    value: '12',
  },
];

type AccountsProps = {
  selectedAgency: AgencyFilters;
};

export const Accounts = (props: AccountsProps) => {
  const {search} = useLocation();
  const getQueryStringValue = parseQueryString(search);
  const {accounts, sort, filters: accountFilters} = useSelector((state: ReducerState) => state.account);
  const {BR_A360_EnrollmentUpload} = useSelector((state: ReducerState) => state.featureFlag);
  const {currentAgentNumbers} = useSelector((state: ReducerState) => state.user.profile);
  const accountNameQuerystringValue = getQueryStringValue('accountName');
  const accountNumberQuerystringValue = getQueryStringValue('accountNumber');
  const groupNumberQuerystringValue = getQueryStringValue('groupNumber');
  const accountName = O.getOrElse(() => accountFilters.name)(accountNameQuerystringValue);
  const accountNumber = O.getOrElse(() => accountFilters.number)(accountNumberQuerystringValue);
  const groupNumber = O.getOrElse(() => accountFilters.groupnumber)(groupNumberQuerystringValue);

  const scopes = useSelector(selectUserScopes);
  const history = useHistory();
  const {selectedAgency} = props;
  const dispatch = useDispatch();
  const {analytics} = useSelector((state: ReducerState) => state.analytics);

  useEffect(() => {
    dispatch(
      changeAccountFilters({
        ...accountFilters,
        name: accountName,
        number: accountNumber,
        groupnumber: groupNumber,
      })
    );
  }, [accountName, accountNumber]);

  useEffect(() => {
    dispatch(
      getAccounts(
        currentAgentNumbers,
        selectedAgency.isNewYork,
        selectedAgency.encryptedIdentifier,
        undefined,
        accountFilters,
        O.toUndefined(sort)
      )
    );
  }, [currentAgentNumbers, selectedAgency.isNewYork, selectedAgency.encryptedIdentifier, accountFilters, sort]);

  useEffect(() => {
    return () => {
      dispatch(clearAccounts());
      dispatch(clearSort());
    };
  }, [clearAccounts, changeAccountFilters]);

  const changeFilter = withValueChangedHook(accountFilters, changeAccountFilters, dispatch);
  const filterProperties = {
    changeFilter,
    filterData: accountFilters,
  };

  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const exportToCsvProps = {
    startIcon: <i className="fa fa-2x fa-file-download" />,
    extension: FileExtension.CSV,
    downloadedFileName: 'FilteredAccounts',
    relativeFileLocation: `/api/employer/v1/accounts/csv${addFilterParameters({
      ...accountFilters,
      agentNumbers: currentAgentNumbers.map(prop('value')),
      isNewYork: selectedAgency.isNewYork,
      encryptedIdentifier: selectedAgency.encryptedIdentifier,
    })}`,
    children: 'Export To CSV',
  };
  return (
    <PagedView
      pagedViewKey="accounts"
      data={accounts}
      sort={O.toUndefined(sort)}
      eventAction="agentAccountSettingFilter"
      onChangeSort={newsort => dispatch(changeSort(newsort))}
      loadMore={page =>
        dispatch(
          getAccounts(
            currentAgentNumbers,
            selectedAgency.isNewYork,
            selectedAgency.encryptedIdentifier,
            page,
            accountFilters,
            O.toUndefined(sort)
          )
        )
      }
      actions={
        isMobile ? (
          <FileDownloadFab variant="extended" color="primary" displayTooltip {...exportToCsvProps} />
        ) : (
          <FileDownloadButton
            variant="contained"
            color="primary"
            displayTooltip
            {...exportToCsvProps}
            exportToCsvEvent="agentAccount_exportToCsv"
          />
        )
      }
    >
      {columnProps => (
        <>
          <Column
            {...columnProps}
            title="Group Number"
            sortColumnKey="groupNumber"
            filterProperties={withStringFilterProperties(filterProperties, 'groupnumber')}
            default
            ddprivacy="mask"
          >
            {account => account.groupNumber}
          </Column>
          <Column
            {...columnProps}
            title="Account Number"
            sortColumnKey="number"
            filterProperties={withStringFilterProperties(filterProperties, 'number')}
            default
            ddprivacy="mask"
          >
            {account => account.number}
          </Column>
          <Column
            {...columnProps}
            title="Account Name"
            sortColumnKey="name"
            filterProperties={withStringFilterProperties(filterProperties, 'name')}
            default
            ddprivacy="mask"
          >
            {account => account.name}
          </Column>
          <Column
            {...columnProps}
            title="Agent of Record"
            sortColumnKey="agentOfRecordNumber"
            filterProperties={withStringFilterProperties(filterProperties, 'agentOfRecordNumber')}
            default
          >
            {account => account.agentOfRecordNumber}
          </Column>
          <Column
            {...columnProps}
            title="Submitted Date"
            filterProperties={withDateFilterProperties(filterProperties, 'lastAppSubmit')}
            default
          >
            {account => toFormattedDateAsUTC(account.lastAppSubmittedDate)}
          </Column>
          <Column {...columnProps} title="Master Policy" default>
            {account => account.policyNumber}
          </Column>
          <Column
            {...columnProps}
            title="Payment Status"
            filterProperties={withSelectFilterProperties(filterProperties, 'paymentStatuses', PaymentStatuses)}
            default
          >
            {account =>
              account.paymentStatus && account.paymentStatus.length > 0 === true
                ? toSentenceCase(account.paymentStatus[0].name ?? '')
                    ?.split('_')
                    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
                    .join(' ')
                : 'Payment Status Missing'
            }
          </Column>
          <Column
            {...columnProps}
            title="Account Status"
            sortColumnKey="status"
            filterProperties={withSelectFilterProperties(filterProperties, 'status', AccountStatuses)}
            default
          >
            {account => toSentenceCase(account.status || '')}
          </Column>
          <Column {...columnProps} isActions title="" default>
            {account => (
              <>
                <RequireScope scopes={scopes} scope={Scope.ViewAccountDetails}>
                  <Link
                    to={{
                      pathname: `accounts/${account.id}/accountNumber/${account.number}`,
                      search: getAccountDetailsQueryParams(
                        getEisFlagFromLinks(account.links),
                        account.groupNumber,
                        account.policyNumber,
                        // @ts-ignore
                        account.source?.name as AccountSourceType
                      ),
                    }}
                  >
                    <Button
                      id={`accounts-view-details-btn-${account.id}`}
                      className="primary-action-button"
                      color="primary"
                      onClick={() => {
                        dispatch(
                          changeAnalyticsState({
                            page: analytics.page,
                            ctaResponse: {eventAction: ANALYTICS_EVENT_ACTION.AGTAC_VIEW_DETAILS},
                          })
                        );
                      }}
                    >
                      View Details
                    </Button>
                  </Link>
                </RequireScope>
                {(scopes.includes(Scope.SearchPolicies) ||
                  scopes.includes(Scope.SearchPolicyCorrespondence) ||
                  (scopes.includes(Scope.UploadEnrollmentApplication) &&
                    Number(getEisFlagFromLinks(account.links)) === DataSource.LEGACY)) && (
                  <ActionsMenu id={`accounts-actions-menu-${account.id}`}>
                    {[
                      scopes.includes(Scope.SearchPolicies) && (
                        <MenuItem
                          id={`accounts-view-policies-btn-${account.id}`}
                          key={`view-policy-${account.number}`}
                          onClick={() => {
                            dispatch(
                              changeAnalyticsState({
                                page: analytics.page,
                                ctaResponse: {eventAction: ANALYTICS_EVENT_ACTION.AGTAC_VIEW_POLICIES},
                              })
                            );
                            history.push(PolicyRouteConstants.POLICIES_BY_ACCOUNT(account.number!));
                          }}
                        >
                          View Policies
                        </MenuItem>
                      ),
                      scopes.includes(Scope.SearchPolicyCorrespondence) && (
                        <MenuItem
                          id={`accounts-view-corres-btn-${account.id}`}
                          key={`view-corres-${account.number}`}
                          onClick={() => {
                            dispatch(
                              changeCorrespondenceFilter({
                                accountNumber: account.number!,
                                policyNumber: '',
                              })
                            );
                            history.push(PolicyRouteConstants.CORRESPONDENCE_SUMMARIES(undefined, account.number!));
                          }}
                        >
                          View Correspondence
                        </MenuItem>
                      ),
                        scopes.includes(Scope.UploadEnrollmentApplication) 
                        && 
                          (
                            (BR_A360_EnrollmentUpload && Number(getEisFlagFromLinks(account.links)) === DataSource.EIS)
                            || Number(getEisFlagFromLinks(account.links)) === DataSource.LEGACY
                          )
                        && (
                        <MenuItem
                          id={`accounts-upload-enrollment-app-btn-${account.id}`}
                          key={`upload-enrollment-application-${account.number}`}
                          onClick={() => {
                            dispatch(
                              changeAnalyticsState({
                                page: analytics.page,
                                ctaResponse: {eventAction: ANALYTICS_EVENT_ACTION.AGTAC_UPLOAD_ENROLLMENT},
                              })
                            );
                            if (Number(getEisFlagFromLinks(account.links)) === DataSource.EIS) { // for EIS accounts
                              dispatch(getCustomerConnectionProfile(
                                account?.customerUri, 
                                account?.number, 
                                account?.name
                              ));
                            } else { // for Legacy accounts
                              dispatch(
                                showModal(
                                  ModalState.UploadEnrollmentApplication({
                                    accountId: account.id!,
                                    accountNumber: account.number!,
                                    servicingAgentNumber: account.servicingAgentNumber![0],
                                  })
                                )
                              );
                            }
                          }}
                        >
                          Upload Enrollments
                        </MenuItem>
                      )
                    ]}
                  </ActionsMenu>
                )}
              </>
            )}
          </Column>
        </>
      )}
    </PagedView>
  );
};

export default Accounts;
